<template>
  <div class="file-management-container">
    <!-- 权限检查 -->
    <div v-if="!isAdminLoggedIn" class="permission-denied">
      <el-alert
        title="权限不足"
        description="请先登录管理员账号"
        type="error"
        show-icon
        :closable="false"
      >
        <template slot="default">
          <el-button type="primary" @click="goToAdminLogin">前往登录</el-button>
        </template>
      </el-alert>
    </div>

    <!-- 主要内容区域 -->
    <div v-else>
      <div class="management-header">
        <h2>文件管理</h2>
        <div class="header-actions">
          <el-button 
            type="success" 
            icon="el-icon-download" 
            @click="showBatchDownloadDialog"
            :disabled="selectedFiles.length === 0"
            v-if="canFileManage"
            size="small"
            class="batch-download-btn"
          >
            批量下载
            <el-badge 
              v-if="selectedFiles.length > 0" 
              :value="selectedFiles.length" 
              type="warning"
              class="batch-badge"
            />
          </el-button>
          <el-button 
            type="primary" 
            icon="el-icon-upload" 
            @click="showUploadDialog"
            v-if="canFileManage"
            size="small"
          >
            上传文件
          </el-button>
          <el-button 
            icon="el-icon-refresh" 
            @click="loadFileList"
            :loading="loading"
            size="small"
          >
            刷新
          </el-button>
          <el-button 
            icon="el-icon-switch-button" 
            @click="handleLogout"
            type="danger"
            size="small"
          >
            退出登录
          </el-button>
        </div>
      </div>

      <!-- 模块切换按钮 -->
      <div class="module-tabs">
        <el-button-group>
          <el-button 
            :type="currentModule === 'all' ? 'primary' : 'default'"
            @click="switchModule('all')"
          >
            全部文件
          </el-button>
          <el-button 
            :type="currentModule === 'internet' ? 'primary' : 'default'"
            @click="switchModule('internet')"
          >
            创新大赛
          </el-button>
          <el-button 
            :type="currentModule === 'xinwenke' ? 'primary' : 'default'"
            @click="switchModule('xinwenke')"
          >
            新文科
          </el-button>
          <el-button 
            :type="currentModule === 'career' ? 'primary' : 'default'"
            @click="switchModule('career')"
          >
            职规大赛
          </el-button>
        </el-button-group>
      </div>

    <!-- 搜索筛选区域 -->
    <div class="filter-section">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <!-- 全部文件模块筛选 -->
        <template v-if="currentModule === 'all'">
          <el-form-item label="文件名称">
            <el-input 
              v-model="searchForm.name" 
              placeholder="请输入文件名称"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="文件格式">
            <el-select 
              v-model="searchForm.format" 
              placeholder="请选择文件格式"
              clearable
            >
              <el-option
                v-for="format in fileFormats"
                :key="format.format"
                :label="format.name"
                :value="format.format"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="排序方式">
            <el-select 
              v-model="searchForm.sort" 
              placeholder="请选择排序方式"
              style="width: 150px;"
            >
              <el-option label="最新" value="new" />
              <el-option label="热门" value="hot" />
              <el-option label="点赞数" value="laud" />
            </el-select>
          </el-form-item>
        </template>

        <!-- 互联网+模块筛选 -->
        <template v-if="currentModule === 'internet'">
          <el-form-item label="文件名称">
            <el-input 
              v-model="searchForm.name" 
              placeholder="请输入文件名称"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="文件格式">
            <el-select 
              v-model="searchForm.format" 
              placeholder="请选择文件格式"
              clearable
            >
              <el-option
                v-for="format in fileFormats"
                :key="format.format"
                :label="format.name"
                :value="format.format"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="排序方式">
            <el-select 
              v-model="searchForm.sort" 
              placeholder="请选择排序方式"
              style="width: 150px;"
            >
              <el-option label="最新" value="new" />
              <el-option label="热门" value="hot" />
              <el-option label="点赞数" value="laud" />
            </el-select>
          </el-form-item>
        </template>

        <!-- 新文科模块筛选 -->
        <template v-if="currentModule === 'xinwenke'">
          <el-form-item label="名称">
            <el-input 
              v-model="searchForm.keyword" 
              placeholder="请输入文件名称"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="作者">
            <el-input 
              v-model="searchForm.author" 
              placeholder="请输入作者名称"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="大分类">
            <el-select 
              v-model="searchForm.categoryId" 
              placeholder="请选择大分类"
              clearable
              @change="onCategoryChange"
              class="xinwenke-category-select"
            >
              <el-option
                v-for="category in categories"
                :key="category.id"
                :label="category.name"
                :value="category.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="子分类">
            <el-select 
              v-model="searchForm.subcategoryId" 
              placeholder="请选择子分类"
              clearable
              class="xinwenke-category-select"
            >
              <el-option
                v-for="subcategory in subcategories"
                :key="subcategory.id"
                :label="subcategory.name"
                :value="subcategory.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="排序方式" class="xinwenke-search-buttons">
            <div class="sort-and-buttons">
              <el-select 
                v-model="searchForm.sort" 
                placeholder="请选择排序方式"
                class="xinwenke-sort-select"
              >
                <el-option label="最新" value="new" />
                <el-option label="热门" value="hot" />
                <el-option label="点赞数" value="laud" />
              </el-select>
              <el-button type="primary" @click="handleSearch">搜索</el-button>
              <el-button @click="resetSearch">重置</el-button>
            </div>
          </el-form-item>
        </template>

        <!-- 职规大赛模块筛选 -->
        <template v-if="currentModule === 'career'">
          <el-form-item label="文件名称">
            <el-input 
              v-model="searchForm.name" 
              placeholder="请输入文件名称"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="文件格式">
            <el-select 
              v-model="searchForm.format" 
              placeholder="请选择文件格式"
              clearable
            >
              <el-option
                v-for="format in fileFormats"
                :key="format.format"
                :label="format.name"
                :value="format.format"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="排序方式">
            <el-select 
              v-model="searchForm.sort" 
              placeholder="请选择排序方式"
              style="width: 150px;"
            >
              <el-option label="最新" value="new" />
              <el-option label="热门" value="hot" />
              <el-option label="点赞数" value="laud" />
            </el-select>
          </el-form-item>
        </template>

        <!-- 通用搜索按钮 (非新文科模块) -->
        <template v-if="currentModule !== 'xinwenke'">
          <el-form-item>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
            <el-button @click="resetSearch">重置</el-button>
          </el-form-item>
        </template>
      </el-form>
    </div>


    <!-- 文件列表 -->
    <div class="file-list-section">
      <el-table 
        :data="fileList" 
        v-loading="loading"
        stripe
        border
        style="width: 100%"
        :key="currentModule"
        :show-overflow-tooltip="false"
        @selection-change="handleSelectionChange"
        ref="fileTable"
        table-layout="fixed"
      >
        <!-- 统一的列定义，根据模块动态显示/隐藏 -->
        
        <!-- 选择列 -->
        <el-table-column 
          type="selection" 
          width="55" 
          align="center"
          :selectable="checkSelectable"
        />
        
        <!-- 文件编码 -->
        <el-table-column 
          prop="code" 
          label="文件编码" 
          :width="currentModule === 'xinwenke' ? 140 : 180" 
          show-overflow-tooltip 
          align="center" 
        />
        
        <!-- 文件名称 -->
        <el-table-column 
          prop="name" 
          label="文件名称" 
          min-width="120"
          show-overflow-tooltip 
          align="center" 
        />
        
        <!-- 封面 -->
        <el-table-column label="封面" width="120" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.cover"
              :src="scope.row.cover"
              :preview-src-list="[scope.row.cover]"
              fit="cover"
              style="width: 60px; height: 40px; border-radius: 4px;"
            />
            <span v-else class="no-cover">无封面</span>
          </template>
        </el-table-column>
        
        <!-- 文件类型 -->
        <el-table-column 
          v-if="currentModule !== 'xinwenke'"
          prop="type" 
          label="文件类型" 
          width="120" 
          align="center"
        >
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 1 ? 'primary' : 'success'">
              {{ scope.row.type === 1 ? 'PPT' : 'Word' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <!-- 文件格式 (仅新文科模块显示) -->
        <el-table-column 
          v-if="currentModule === 'xinwenke'"
          prop="format" 
          label="文件格式" 
          width="100" 
          align="center"
        >
          <template slot-scope="scope">
            <el-tag type="info">{{ scope.row.format || '-' }}</el-tag>
          </template>
        </el-table-column>
        
        <!-- 分类 (仅新文科模块显示) -->
        <el-table-column 
          v-if="currentModule === 'xinwenke'"
          prop="categoryName" 
          label="分类" 
          width="80" 
          align="center" 
        />
        
        <!-- 类别 (仅新文科模块显示) -->
        <el-table-column 
          v-if="currentModule === 'xinwenke'"
          prop="subcategoryName" 
          label="类别" 
          width="80" 
          align="center" 
        />
        
        <!-- 页数 -->
        <el-table-column prop="page" label="页数" width="80" align="center" />
        
        <!-- 价格 -->
        <el-table-column 
          prop="price" 
          label="价格" 
          :width="currentModule === 'xinwenke' ? 70 : 80" 
          align="center"
        >
          <template slot-scope="scope">
            <span class="price">¥{{ scope.row.price }}</span>
          </template>
        </el-table-column>
        
        <!-- 上传时间 -->
        <el-table-column prop="createTime" label="上传时间" width="120" align="center">
          <template slot-scope="scope">
            {{ formatDateShort(scope.row.createTime) }}
          </template>
        </el-table-column>
        
        <!-- 作者 -->
        <el-table-column 
          prop="author" 
          label="作者" 
          width="80"
          show-overflow-tooltip
          align="center" 
        />
        
        <!-- 状态 -->
        <el-table-column prop="state" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.state === 1 ? 'success' : 'danger'">
              {{ scope.row.state === 1 ? '正常' : '下架' }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column label="操作" :width="currentModule === 'internet' ? 300 : 240" align="center">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button 
                size="mini" 
                type="primary" 
                @click="viewFile(scope.row)"
                class="action-btn"
              >
                查看
              </el-button>
              <el-button 
                size="mini" 
                type="success" 
                @click="downloadFile(scope.row)"
                class="action-btn"
              >
                下载
              </el-button>
              <el-button 
                size="mini" 
                type="warning" 
                @click="editFile(scope.row)"
                v-if="canFileManage"
                class="action-btn"
              >
                修改
              </el-button>
              <el-button 
                size="mini" 
                type="danger" 
                @click="deleteFile(scope.row)"
                v-if="canFileManage"
                class="action-btn"
              >
                删除
              </el-button>
              <el-button 
                size="mini" 
                type="info" 
                @click="manageFragments(scope.row)"
                v-if="canFileManage && currentModule === 'internet'"
                class="action-btn"
              >
                片段管理
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>


      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
        />
      </div>
    </div>

    <!-- 文件预览对话框 -->
    <el-dialog
      :title="currentFile ? `文件预览 - ${currentFile.name}` : '文件预览'"
      :visible.sync="previewDialogVisible"
      width="1200px"
      :lock-scroll="false"
      :close-on-click-modal="false"
      :before-close="onPreviewDialogClose"
      class="file-preview-dialog"
      :center="true"
    >
      <div v-if="currentFile" class="file-preview-container">
        <!-- 文件信息头部 -->
        <div class="file-info-header">
          <div class="file-basic-info">
            <div class="file-title-row">
              <h3 class="file-title">{{ currentFile.name }}</h3>
            </div>
            <div class="file-meta-row">
              <span class="file-format">
                <i class="el-icon-files"></i>
                {{ currentFile.format?.toUpperCase() || '未知格式' }}
              </span>
              <span class="file-author">
                <i class="el-icon-user"></i>
                {{ currentFile.author }}
              </span>
              <span class="file-price">
                <i class="el-icon-money"></i>
                ¥{{ currentFile.price }}
              </span>
              <span class="file-pages" v-if="currentFile.pageCount">
                <i class="el-icon-document"></i>
                {{ currentFile.pageCount }}页
              </span>
            </div>
          </div>
          <div class="file-actions">
            <el-button type="primary" @click="downloadFile(currentFile)">
              <i class="el-icon-download"></i> 下载文件
            </el-button>
            <el-button @click="openInNewTab(currentFile)">
              <i class="el-icon-view"></i> 新窗口预览
            </el-button>
          </div>
        </div>

        <!-- 文件预览区域 -->
        <div class="file-preview-area">
          <!-- 预览图展示 -->
          <div v-if="previewImages.length > 0" class="preview-images">
            <el-carousel 
              :interval="0" 
              arrow="hover" 
              indicator-position="outside"
              height="500px"
              class="preview-carousel"
            >
              <el-carousel-item v-for="(image, index) in previewImages" :key="index">
                <div class="preview-image-container">
                  <img
                    :src="image"
                    class="preview-image"
                    :alt="`预览图 ${index + 1}`"
                    @click="openImagePreview(index)"
                    @load="onImageLoad"
                    @error="onImageError"
                  />
                </div>
              </el-carousel-item>
            </el-carousel>
          </div>

          <!-- 在线预览 -->
          <div v-else-if="currentFile.url && canPreviewOnline" class="online-preview">
            <iframe
              :src="getPreviewUrl(currentFile)"
              frameborder="0"
              class="preview-iframe"
              @load="onPreviewLoad"
              @error="onPreviewError"
            ></iframe>
          </div>

          <!-- 不支持预览的提示 -->
          <div v-else class="no-preview">
            <div class="no-preview-content">
              <i class="el-icon-document" style="font-size: 64px; color: #ccc;"></i>
              <p>该文件格式暂不支持在线预览</p>
              <p class="no-preview-tip">请下载文件后使用相应软件打开查看</p>
              <el-button type="primary" @click="downloadFile(currentFile)">
                <i class="el-icon-download"></i> 下载文件
              </el-button>
            </div>
          </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="previewLoading" class="preview-loading">
          <el-loading
            element-loading-text="正在加载文件预览..."
            element-loading-spinner="el-icon-loading"
            element-loading-background="rgba(0, 0, 0, 0.8)"
          />
        </div>
      </div>
    </el-dialog>

    <!-- 图片预览器 -->
    <el-image-viewer
      v-if="imageViewerVisible"
      :url-list="previewImages"
      :initial-index="currentImageIndex"
      @close="closeImageViewer"
    />

    <!-- 文件上传对话框 -->
    <el-dialog
      title="上传文件"
      :visible.sync="uploadDialogVisible"
      width="900px"
      :close-on-click-modal="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      :lock-scroll="false"
      :top="'1vh'"
      @close="onUploadDialogClose"
      class="upload-dialog"
    >
      <FileUpload ref="fileUpload" :module="currentModule" @upload-success="onUploadSuccess" @upload-cancel="onUploadCancel" />
    </el-dialog>

    <!-- 文件编辑对话框 -->
    <el-dialog
      title="编辑文件"
      :visible.sync="editDialogVisible"
      width="900px"
      :close-on-click-modal="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      :lock-scroll="false"
      :top="'1vh'"
      @close="onEditDialogClose"
      class="edit-dialog"
    >
      <FileEdit 
        :module="currentModule" 
        :file-data="editingFile"
        @edit-success="onEditSuccess" 
        @edit-cancel="onEditCancel" 
      />
    </el-dialog>

    <!-- 批量下载对话框 -->
    <el-dialog
      title="批量下载文件"
      :visible.sync="batchDownloadDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      :lock-scroll="false"
      :top="'10vh'"
      @close="onBatchDownloadDialogClose"
      class="batch-download-dialog"
    >
      <div class="batch-download-content">
        <!-- 选择文件列表 -->
        <div class="selected-files-section">
          <h4>已选择的文件 ({{ selectedFiles.length }} 个)</h4>
          <div class="selected-files-list">
            <el-tag
              v-for="file in selectedFiles"
              :key="file.code"
              closable
              @close="removeSelectedFile(file)"
              class="file-tag"
            >
              {{ file.name }} ({{ file.code }})
            </el-tag>
          </div>
        </div>

        <!-- 下载说明 -->
        <div class="download-info-section">
          <el-alert
            title="批量下载说明"
            type="info"
            :closable="false"
            show-icon
          >
            <div slot="description">
              <p>• 文件将自动打包为ZIP格式直接下载</p>
              <p>• 支持新旧模块文件混合打包</p>
              <p>• 下载过程中请勿关闭浏览器</p>
              <p>• 单次最多支持50个文件</p>
            </div>
          </el-alert>
        </div>

        <!-- 下载状态 -->
        <div v-if="batchDownloading" class="download-status-section">
          <el-alert
            title="正在打包下载..."
            type="warning"
            :closable="false"
            show-icon
          >
            <div slot="description">
              <p>正在将 {{ selectedFiles.length }} 个文件打包为ZIP，请稍候...</p>
            </div>
          </el-alert>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div slot="footer" class="dialog-footer">
        <el-button @click="onBatchDownloadDialogClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="startBatchDownload"
          :loading="batchDownloading"
          :disabled="selectedFiles.length === 0"
        >
          {{ batchDownloading ? '下载中...' : '开始下载' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 片段管理对话框 -->
    <el-dialog
      title="片段管理"
      :visible.sync="fragmentManagementDialogVisible"
      width="1200px"
      :close-on-click-modal="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      :lock-scroll="false"
      :top="'5vh'"
      @close="onFragmentManagementDialogClose"
      class="fragment-management-dialog"
    >
      <div class="fragment-management-content">
        <!-- 当前文件信息 -->
        <div class="current-file-info">
          <h4>当前文件：{{ currentFragmentFile?.name || '-' }}</h4>
          <span class="file-type-badge" :class="currentFragmentFile?.type === 1 ? 'ppt' : 'word'">
            {{ currentFragmentFile?.type === 1 ? 'PPT' : 'Word' }}
          </span>
        </div>

        <!-- 操作工具栏 -->
        <div class="fragment-toolbar">
          <el-button 
            type="primary" 
            icon="el-icon-plus" 
            @click="showAddFragmentDialog"
            size="small"
          >
            新增片段
          </el-button>
          <el-button 
            icon="el-icon-refresh" 
            @click="loadFragmentList"
            :loading="fragmentLoading"
            size="small"
          >
            刷新
          </el-button>
        </div>

        <!-- 片段列表 -->
        <div class="fragment-list-section">
          <el-table 
            :data="fragmentList" 
            v-loading="fragmentLoading"
            stripe
            border
            style="width: 100%"
            :show-overflow-tooltip="false"
          >
            <el-table-column 
              prop="code" 
              label="片段编码" 
              width="120" 
              show-overflow-tooltip 
              align="center" 
            />
            
            <el-table-column 
              prop="name" 
              label="片段名称" 
              min-width="150"
              show-overflow-tooltip 
              align="center" 
            />
            
            <el-table-column 
              prop="description" 
              label="简介" 
              min-width="150"
              show-overflow-tooltip 
              align="center" 
            />
            
            <el-table-column 
              prop="page" 
              label="页数" 
              width="80" 
              align="center" 
            />
            
            <el-table-column 
              prop="hot" 
              label="是否精选" 
              width="100" 
              align="center"
            >
              <template slot-scope="scope">
                <el-tag :type="scope.row.hot === 1 ? 'success' : 'info'">
                  {{ scope.row.hot === 1 ? '是' : '否' }}
                </el-tag>
              </template>
            </el-table-column>
            
            <el-table-column 
              prop="price" 
              label="价格" 
              width="100" 
              align="center"
            >
              <template slot-scope="scope">
                <span class="price">¥{{ scope.row.price }}</span>
              </template>
            </el-table-column>
            
            <el-table-column 
              prop="author" 
              label="作者" 
              width="100"
              align="center" 
            />
            
            <el-table-column label="操作" width="180" align="center">
              <template slot-scope="scope">
                <el-button 
                  size="mini" 
                  type="warning" 
                  @click="editFragment(scope.row)"
                  class="fragment-action-btn"
                >
                  修改
                </el-button>
                <el-button 
                  size="mini" 
                  type="danger" 
                  @click="deleteFragment(scope.row)"
                  class="fragment-action-btn"
                >
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="fragment-pagination">
            <el-pagination
              @size-change="handleFragmentSizeChange"
              @current-change="handleFragmentCurrentChange"
              :current-page="fragmentPagination.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="fragmentPagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="fragmentPagination.total"
              small
            />
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 片段新增/编辑对话框 -->
    <el-dialog
      :title="fragmentFormMode === 'add' ? '新增片段' : '修改片段'"
      :visible.sync="fragmentFormDialogVisible"
      width="900px"
      :close-on-click-modal="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      :lock-scroll="false"
      :top="'5vh'"
      @close="onFragmentFormDialogClose"
      class="fragment-form-dialog"
    >
      <FragmentForm 
        :mode="fragmentFormMode"
        :file-type="currentFragmentFile?.type"
        :parent-code="currentFragmentFile?.code"
        :fragment-data="editingFragment"
        @form-success="onFragmentFormSuccess" 
        @form-cancel="onFragmentFormCancel" 
      />
    </el-dialog>
    </div>
  </div>
</template>

<script>
import api from '@/api/api';
import FileUpload from '@/components/admin/FileUpload.vue';
import FileEdit from '@/components/admin/FileEdit.vue';
import FragmentForm from '@/components/admin/FragmentForm.vue';
import { 
  isAdminLoggedIn, 
  hasAdminPermission, 
  getAdminAuthState,
  adminLogout 
} from '@/utils/adminAuth';

export default {
  name: 'FileManagement',
  components: {
    FileUpload,
    FileEdit,
    FragmentForm
  },
  data() {
    return {
      loading: false,
      initializing: false, // 防止重复初始化
      fileList: [],
      categories: [],
      subcategories: [],
      allSubcategories: [], // 存储所有子分类数据
      fileFormats: [],
      currentFile: null,
      previewDialogVisible: false,
      previewImages: [],
      previewLoading: false,
      imageViewerVisible: false,
      currentImageIndex: 0,
      uploadDialogVisible: false,
      editDialogVisible: false,
      editingFile: null, // 正在编辑的文件数据
      currentModule: 'all', // 当前模块：all, internet, xinwenke, career
      // 批量下载相关
      selectedFiles: [], // 选中的文件列表
      batchDownloadDialogVisible: false, // 批量下载对话框显示状态
      batchDownloading: false, // 批量下载进行中
      searchForm: {
        name: '',
        keyword: '',
        author: '',
        categoryId: null,
        subcategoryId: null,
        type: null,
        format: null,
        sort: 'new'
      },
      pagination: {
        pageNum: 1,
        pageSize: 20,
        total: 0
      },
      // 片段管理相关
      fragmentManagementDialogVisible: false,
      fragmentFormDialogVisible: false,
      currentFragmentFile: null, // 当前管理的文件
      fragmentList: [], // 片段列表
      fragmentLoading: false,
      fragmentFormMode: 'add', // 'add' 或 'edit'
      editingFragment: null, // 正在编辑的片段数据
      fragmentPagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      tokenCheckTimer: null // token检查定时器
    };
  },
  computed: {
    // 检查管理员登录状态
    isAdminLoggedIn() {
      return isAdminLoggedIn();
    },
    // 检查文件管理权限
    canFileManage() {
      return hasAdminPermission('canFileManage');
    }
  },
  mounted() {
    // 使用nextTick确保DOM完全渲染后再加载数据
    this.$nextTick(() => {
      this.checkPermissionAndLoadData();
    });
    
    // 设置定时器，定期检查token是否过期（每30秒检查一次）
    this.tokenCheckTimer = setInterval(() => {
      this.checkTokenValidity();
    }, 30000); // 30秒检查一次
  },
  beforeDestroy() {
    // 组件销毁前的清理工作
    if (this.tokenCheckTimer) {
      clearInterval(this.tokenCheckTimer);
    }
  },
  methods: {
    // 检查token有效性
    checkTokenValidity() {
      // 如果当前不在管理员页面，不检查
      if (!this.isAdminLoggedIn) {
        // token已失效，清除定时器并跳转到登录页
        if (this.tokenCheckTimer) {
          clearInterval(this.tokenCheckTimer);
          this.tokenCheckTimer = null;
        }
        this.$message.warning('管理员登录已过期，请重新登录');
        this.$router.push({ name: 'AdminLogin' });
        return;
      }
    },

    // 检查权限并加载数据
    async checkPermissionAndLoadData() {
      // 防止重复初始化
      if (this.initializing) {
        return;
      }
      
      this.initializing = true;
      
      try {
        // 先检查token有效性
        this.checkTokenValidity();
        
        // 检查管理员登录状态
        if (!this.isAdminLoggedIn) {
          this.$message.warning('请先登录管理员账号');
          this.$router.push({ name: 'AdminLogin' });
          return;
        }

        // 检查文件管理权限
        if (!this.canFileManage) {
          this.$message.warning('您没有文件管理权限');
          return;
        }

        // 加载数据
        await this.loadCategories();
        await this.loadFileFormats();
        await this.loadSubcategories();
        await this.loadFileList();
      } finally {
        this.initializing = false;
      }
    },

    // 前往管理员登录页
    goToAdminLogin() {
      this.$router.push({ name: 'AdminLogin' });
    },

    // 管理员登出
    async handleLogout() {
      try {
        await this.$confirm('确定要退出管理员登录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        await adminLogout();
        this.$message.success('已退出管理员登录');
        this.$router.push({ name: 'AdminLogin' });
      } catch (error) {
        if (error !== 'cancel') {
          // 登出失败，静默处理
          this.$message.error('登出失败');
        }
      }
    },

    // 加载分类数据
    async loadCategories() {
      try {
        const response = await api.getCategories();
        if (response.data && response.data.code === 200) {
          this.categories = response.data.data || [];
        }
      } catch (error) {
        // 加载分类失败，静默处理
        this.$message.error('加载分类失败');
      }
    },

    // 加载文件格式数据
    async loadFileFormats() {
      try {
        const response = await api.getFileFormats();
        if (response.data && response.data.code === 200) {
          this.fileFormats = response.data.data || [];
        }
      } catch (error) {
        // 加载文件格式失败，静默处理
        this.$message.error('加载文件格式失败');
      }
    },

    // 加载子分类数据
    async loadSubcategories() {
      try {
        const response = await api.getSubcategories();
        if (response.data && response.data.code === 200) {
          this.allSubcategories = response.data.data || [];
        } else {
          // 子分类接口返回错误，静默处理
        }
      } catch (error) {
        // 加载子分类失败，静默处理
        this.$message.error('加载子分类失败');
      }
    },


    // 搜索
    handleSearch() {
      this.pagination.pageNum = 1;
      this.loadFileList();
    },



    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.pageNum = 1;
      this.loadFileList();
    },

    // 当前页变化
    handleCurrentChange(val) {
      this.pagination.pageNum = val;
      this.loadFileList();
    },

    // 查看文件预览
    async viewFile(file) {
      try {
        this.previewLoading = true;
        const response = await api.getXinWenKeFileDetail({ code: file.code });
        if (response.data && response.data.code === 200) {
          this.currentFile = response.data.data;
          this.preparePreviewData();
          this.previewDialogVisible = true;
        } else {
          this.$message.error(response.data?.msg || '获取文件详情失败');
        }
      } catch (error) {
        // 获取文件详情失败，静默处理
        this.$message.error('获取文件详情失败');
      } finally {
        this.previewLoading = false;
      }
    },

    // 修改文件
    async editFile(file) {
      if (!this.canFileManage) {
        this.showPermissionDenied('文件修改');
        return;
      }
      
      try {
        // 根据模块获取文件详情（使用统一的接口，支持新旧模块自动区分）
        let response;
        switch (this.currentModule) {
          case 'xinwenke':
            response = await api.getXinWenKeFileDetail({ code: file.code });
            break;
          case 'internet':
            response = await api.getSourceIndexFileDetail({ code: file.code });
            break;
          case 'career':
            response = await api.getSourceZyhgFileDetail({ code: file.code });
            break;
          default:
            response = await api.getXinWenKeFileDetail({ code: file.code });
        }
        
        if (response.data && response.data.code === 200) {
          this.editingFile = response.data.data;
          this.editDialogVisible = true;
        } else {
          this.$message.error(response.data?.msg || '获取文件详情失败');
        }
      } catch (error) {
        this.$message.error('获取文件详情失败');
      }
    },

    // 删除文件
    deleteFile(file) {
      this.$confirm(`确定要删除文件"${file.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await api.deleteXinWenKeFile({ id: file.id });
          if (response.data && response.data.code === 200) {
            this.$message.success('删除成功');
            this.loadFileList();
          } else {
            this.$message.error(response.data?.msg || '删除失败');
          }
        } catch (error) {
          // 删除文件失败，静默处理
          this.$message.error('删除失败');
        }
      }).catch(() => {
        // 用户取消删除
      });
    },

    // 下载文件
    downloadFile(file) {
      if (file.url) {
        window.open(file.url, '_blank');
      } else {
        this.$message.warning('文件链接不存在');
      }
    },

    // 预览文件（不下载）
    async previewFile(file) {
      try {
        const response = await api.previewXinWenKeFile({ code: file.code });
        if (response.data && response.data.code === 200) {
          // 如果返回的是预览URL，直接打开
          if (response.data.data && response.data.data.previewUrl) {
            window.open(response.data.data.previewUrl, '_blank');
          } else {
            // 如果没有预览URL，使用原有的预览逻辑
            this.openInNewTab(file);
          }
        } else {
          this.$message.error(response.data?.msg || '获取预览链接失败');
        }
      } catch (error) {
        this.$message.error('获取预览链接失败');
      }
    },

    // 准备预览数据
    preparePreviewData() {
      this.previewImages = [];
      if (this.currentFile && this.currentFile.image) {
        // 处理多张预览图
        const images = this.currentFile.image.split(',').filter(img => img.trim());
        this.previewImages = images.map(img => img.trim());
      }
    },

    // 获取预览URL
    getPreviewUrl(file) {
      if (!file.url) return '';
      
      const fileExtension = file.format?.toLowerCase();
      
      // 根据文件类型选择不同的预览方式
      switch (fileExtension) {
        case 'pdf':
          // PDF文件直接预览
          return file.url;
        case 'ppt':
        case 'pptx':
          // PowerPoint文件使用Office Online预览
          return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
        case 'doc':
        case 'docx':
          // Word文档使用Office Online预览
          return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
        case 'xls':
        case 'xlsx':
          // Excel文件使用Office Online预览
          return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'webp':
          // 图片文件直接预览
          return file.url;
        default:
          // 其他格式不支持在线预览，返回原链接
          return file.url;
      }
    },

    // 检查是否支持在线预览
    canPreviewOnline() {
      if (!this.currentFile || !this.currentFile.url) return false;
      
      const fileExtension = this.currentFile.format?.toLowerCase();
      const supportedFormats = ['pdf', 'ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
      
      return supportedFormats.includes(fileExtension);
    },

    // 在新窗口打开文件（预览模式）
    async openInNewTab(file) {
      try {
        // 首先尝试使用预览API
        const response = await api.previewXinWenKeFile({ code: file.code });
        if (response.data && response.data.code === 200) {
          // 如果返回的是预览URL，直接打开
          if (response.data.data && response.data.data.previewUrl) {
            window.open(response.data.data.previewUrl, '_blank');
            return;
          }
        }
        
        // 如果预览API失败或没有预览URL，使用原有的逻辑
        if (file.url) {
          // 对于支持在线预览的文件格式，使用预览URL
          const previewUrl = this.getPreviewUrl(file);
          if (previewUrl !== file.url) {
            window.open(previewUrl, '_blank');
          } else {
            // 对于不支持预览的格式，直接打开原链接
            window.open(file.url, '_blank');
          }
        } else {
          this.$message.warning('文件链接不存在');
        }
      } catch (error) {
        // 预览API调用失败，使用备用方案
        if (file.url) {
          const previewUrl = this.getPreviewUrl(file);
          window.open(previewUrl, '_blank');
        } else {
          this.$message.warning('文件链接不存在');
        }
      }
    },

    // 预览加载完成
    onPreviewLoad() {
      this.previewLoading = false;
    },

    // 预览加载失败
    onPreviewError() {
      this.previewLoading = false;
      this.$message.error('文件预览加载失败');
    },

    // 预览对话框关闭
    onPreviewDialogClose() {
      this.previewDialogVisible = false;
      this.currentFile = null;
      this.previewImages = [];
      this.previewLoading = false;
    },

    // 打开图片预览
    openImagePreview(index) {
      this.currentImageIndex = index;
      this.imageViewerVisible = true;
    },

    // 关闭图片预览器
    closeImageViewer() {
      this.imageViewerVisible = false;
      this.currentImageIndex = 0;
    },

    // 图片加载完成
    onImageLoad() {
      // 图片加载完成后的处理
    },

    // 图片加载失败
    onImageError(event) {
      // 图片加载失败，静默处理
      this.$message.error('图片加载失败');
    },

    // 显示上传对话框
    showUploadDialog() {
      if (!this.canFileManage) {
        this.showPermissionDenied('文件上传');
        return;
      }
      this.uploadDialogVisible = true;
    },

    // 上传成功回调
    onUploadSuccess() {
      // 清空表单
      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.resetForm();
      }
      // 关闭模态窗
      this.uploadDialogVisible = false;
      // 刷新文件列表
      this.loadFileList();
    },

    // 取消上传
    onUploadCancel() {
      this.uploadDialogVisible = false;
    },

    // 上传对话框关闭
    onUploadDialogClose() {
      // 可以在这里做一些清理工作
    },

    // 编辑成功回调
    onEditSuccess() {
      this.editDialogVisible = false;
      this.editingFile = null;
      this.loadFileList();
      this.$message.success('文件修改成功！');
    },

    // 取消编辑
    onEditCancel() {
      this.editDialogVisible = false;
      this.editingFile = null;
    },

    // 编辑对话框关闭
    onEditDialogClose() {
      this.editingFile = null;
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN');
    },

    // 格式化日期为简短格式（只显示年月日）
    formatDateShort(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },

    // ==================== 批量下载相关方法 ====================
    
    // 表格选择变化处理
    handleSelectionChange(selection) {
      this.selectedFiles = selection;
    },

    // 检查文件是否可选择
    checkSelectable(row) {
      // 所有文件都可以选择
      return true;
    },

    // 显示批量下载对话框
    showBatchDownloadDialog() {
      if (this.selectedFiles.length === 0) {
        this.$message.warning('请先选择要下载的文件');
        return;
      }
      
      // 检查文件数量限制
      if (this.selectedFiles.length > 50) {
        this.$message.warning('批量下载最多支持50个文件，请重新选择');
        return;
      }
      
      this.batchDownloadDialogVisible = true;
    },

    // 关闭批量下载对话框
    onBatchDownloadDialogClose() {
      this.batchDownloadDialogVisible = false;
    },

    // 移除选中的文件
    removeSelectedFile(file) {
      const index = this.selectedFiles.findIndex(f => f.code === file.code);
      if (index > -1) {
        this.selectedFiles.splice(index, 1);
        // 同步更新表格选择状态
        this.$nextTick(() => {
          this.$refs.fileTable.toggleRowSelection(file, false);
        });
      }
    },

    // 清空选择
    clearSelection() {
      this.selectedFiles = [];
      this.$refs.fileTable.clearSelection();
    },

    // 开始批量下载
    async startBatchDownload() {
      if (this.selectedFiles.length === 0) {
        this.$message.warning('请先选择要下载的文件');
        return;
      }

      try {
        this.batchDownloading = true;
        
        // 获取文件编码列表
        const codes = this.selectedFiles.map(file => file.code);
        
        // 调用批量下载API
        const response = await api.batchDownloadFiles({ codes });
        
        // 处理blob响应
        const blob = new Blob([response.data], { type: 'application/zip' });
        
        // 创建下载链接
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // 从响应头获取文件名，如果没有则使用默认名称
        const contentDisposition = response.headers['content-disposition'];
        let fileName = `批量下载_${new Date().toISOString().slice(0, 10)}.zip`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="(.+)"/);
          if (match) {
            fileName = match[1];
          }
        }
        
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$message.success('ZIP文件下载已开始');
        this.batchDownloadDialogVisible = false;
        
      } catch (error) {
        console.error('批量下载失败:', error);
        this.$message.error('批量下载失败：' + (error.message || '未知错误'));
      } finally {
        this.batchDownloading = false;
      }
    },


    // 切换模块
    switchModule(module) {
      this.currentModule = module;
      this.resetSearch();
    },

    // 根据当前模块加载文件列表
    async loadFileList() {
      this.loading = true;
      try {
        // 根据模块构建不同的查询参数
        let params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        };


        // 为不同模块添加特定的查询参数
        if (this.currentModule === 'all') {
          // 全部文件模块：文件名 + 文件格式 + 排序
          if (this.searchForm.name) {
            params.keyword = this.searchForm.name;
          }
          if (this.searchForm.format) {
            params.format = this.searchForm.format;
          }
          // 确保排序参数总是被传递
          params.sort = this.searchForm.sort || 'new';
        } else if (this.currentModule === 'internet') {
          // 互联网+模块：文件名 + 文件格式 + 排序
          if (this.searchForm.name) {
            params.keyword = this.searchForm.name;
          }
          if (this.searchForm.format) {
            params.format = this.searchForm.format;
          }
          // 确保排序参数总是被传递
          params.sort = this.searchForm.sort || 'new';
        } else if (this.currentModule === 'xinwenke') {
          // 新文科模块：保持原有查询参数
          params = {
            ...params,
            ...this.searchForm
          };
          // 确保排序参数总是被传递
          if (!params.sort) {
            params.sort = 'new';
          }
        } else if (this.currentModule === 'career') {
          // 职规大赛模块：文件名 + 文件格式 + 排序
          if (this.searchForm.name) {
            params.keyword = this.searchForm.name;
          }
          if (this.searchForm.format) {
            params.format = this.searchForm.format;
          }
          // 确保排序参数总是被传递
          params.sort = this.searchForm.sort || 'new';
        }


        let response;
        
        try {
          switch (this.currentModule) {
            case 'all':
              // 全部文件 - 使用原有接口
              response = await api.getXinWenKeFileList(params);
              break;
            case 'internet':
              // 互联网+模块
              response = await api.getSourceIndexFiles(params);
              break;
            case 'xinwenke':
              // 新文科模块 - 使用POST请求体传递参数
              response = await api.queryXinWenKeFiles(params);
              break;
            case 'career':
              // 职规大赛模块
              response = await api.getSourceZyhgFiles(params);
              break;
            default:
              response = await api.getXinWenKeFileList(params);
          }
        } catch (apiError) {
          // API调用失败，静默处理
          throw apiError;
        }

        if (response.data && response.data.code === 200) {
          // 根据不同模块处理响应数据
          if (this.currentModule === 'xinwenke') {
            this.fileList = response.data.data?.files || [];
            this.pagination.total = response.data.data?.total || 0;
          } else {
            this.fileList = response.data.data?.records || response.data.data?.list || [];
            this.pagination.total = response.data.data?.total || 0;
          }
        } else {
          this.$message.error(response.data?.msg || '加载文件列表失败');
        }
      } catch (error) {
        // 加载文件列表失败，静默处理
        this.$message.error('加载文件列表失败');
      } finally {
        this.loading = false;
      }
    },

    // 重置搜索表单
    resetSearch() {
      this.searchForm = {
        name: '',
        keyword: '',
        author: '',
        categoryId: null,
        subcategoryId: null,
        type: null,
        format: null,
        sort: 'new'
      };
      this.subcategories = [];
      this.pagination.pageNum = 1;
      // 重置后自动执行查询，显示默认结果
      this.loadFileList();
    },

    // 分类变化处理（新文科模块）
    async onCategoryChange(categoryId) {
      if (this.currentModule === 'xinwenke') {
        // 新文科模块：大分类和子分类是独立的，不需要联动
        this.searchForm.subcategoryId = null;
        this.subcategories = [];
        
        if (categoryId) {
          // 从已加载的所有子分类中过滤出属于当前分类的子分类
          this.subcategories = this.allSubcategories.filter(sub => sub.categoryId === categoryId);
        }
      } else {
        // 其他模块保持原有逻辑
        this.searchForm.subcategoryId = null;
        this.subcategories = [];
        
        if (categoryId) {
          // 从已加载的所有子分类中过滤出属于当前分类的子分类
          this.subcategories = this.allSubcategories.filter(sub => sub.categoryId === categoryId);
        }
      }
    },

    // ==================== 片段管理相关方法 ====================
    
    // 打开片段管理对话框
    async manageFragments(file) {
      if (!this.canFileManage) {
        this.$message.warning('您没有文件管理权限');
        return;
      }
      
      this.currentFragmentFile = file;
      // 切换文件时重置片段分页
      this.fragmentPagination.pageNum = 1;
      this.fragmentManagementDialogVisible = true;
      // 加载片段列表
      await this.loadFragmentList();
    },

    // 加载片段列表
    async loadFragmentList() {
      if (!this.currentFragmentFile || !this.currentFragmentFile.code) {
        return;
      }
      
      this.fragmentLoading = true;
      try {
        const response = await api.getFragmentList({
          parentCode: this.currentFragmentFile.code,
          pageNum: this.fragmentPagination.pageNum,
          pageSize: this.fragmentPagination.pageSize
        });

        if (response.data && response.data.code === 200) {
          const data = response.data.data || {};
          const list = data.records || data.list || data.files || data.rows || data || [];
          this.fragmentList = Array.isArray(list) ? list.map(this.normalizeFragment) : [];
          this.fragmentPagination.total = data.total || data.totalCount || 0;
        } else {
          this.$message.error(response.data?.msg || '加载片段列表失败');
          this.fragmentList = [];
          this.fragmentPagination.total = 0;
        }
      } catch (error) {
        this.$message.error('加载片段列表失败');
      } finally {
        this.fragmentLoading = false;
      }
    },

    // 显示新增片段对话框
    showAddFragmentDialog() {
      this.fragmentFormMode = 'add';
      this.editingFragment = null;
      this.fragmentFormDialogVisible = true;
    },

    // 编辑片段
    async editFragment(fragment) {
      try {
        const response = await api.getFragmentDetail({ code: fragment.code });
        if (response.data && response.data.code === 200) {
          this.editingFragment = this.normalizeFragment(response.data.data || fragment);
        } else {
          this.$message.error(response.data?.msg || '获取片段详情失败');
          return;
        }
        this.fragmentFormMode = 'edit';
        this.fragmentFormDialogVisible = true;
      } catch (error) {
        this.$message.error('获取片段详情失败');
      }
    },

    // 删除片段
    deleteFragment(fragment) {
      this.$confirm(`确定要删除片段"${fragment.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await api.deleteFragment({ code: fragment.code });
          if (response.data && response.data.code === 200) {
            this.$message.success('删除成功');
            this.loadFragmentList();
          } else {
            this.$message.error(response.data?.msg || '删除失败');
          }
        } catch (error) {
          this.$message.error('删除失败');
        }
      }).catch(() => {
        // 用户取消删除
      });
    },

    // 片段表单成功回调
    onFragmentFormSuccess() {
      this.fragmentFormDialogVisible = false;
      this.editingFragment = null;
      this.loadFragmentList();
      this.$message.success(this.fragmentFormMode === 'add' ? '片段添加成功！' : '片段修改成功！');
    },

    // 取消片段表单
    onFragmentFormCancel() {
      this.fragmentFormDialogVisible = false;
      this.editingFragment = null;
    },

    // 片段管理对话框关闭
    onFragmentManagementDialogClose() {
      this.currentFragmentFile = null;
      this.fragmentList = [];
      this.fragmentPagination = {
        pageNum: 1,
        pageSize: 10,
        total: 0
      };
    },

    // 片段表单对话框关闭
    onFragmentFormDialogClose() {
      this.editingFragment = null;
    },

    // 片段数据字段兼容转换
    normalizeFragment(fragment) {
      if (!fragment) return {};
      return {
        ...fragment,
        code: fragment.code,
        name: fragment.name,
        description: fragment.describes || fragment.description || '',
        page: fragment.pageCount !== undefined ? fragment.pageCount : (fragment.page || null),
        hot: fragment.hot !== undefined ? fragment.hot : 0,
        price: fragment.price !== undefined ? fragment.price : null,
        author: fragment.author || '',
        remark: fragment.remark || '',
        tagId: fragment.tag1Id !== undefined ? fragment.tag1Id : (fragment.tagId || null),
        groupId: fragment.tag2Id !== undefined ? fragment.tag2Id : (fragment.groupId || null),
        animation: fragment.animation !== undefined ? fragment.animation : null
      };
    },

    // 片段分页大小变化
    handleFragmentSizeChange(val) {
      this.fragmentPagination.pageSize = val;
      this.fragmentPagination.pageNum = 1;
      this.loadFragmentList();
    },

    // 片段当前页变化
    handleFragmentCurrentChange(val) {
      this.fragmentPagination.pageNum = val;
      this.loadFragmentList();
    }
  }
};
</script>

<style scoped>
.file-management-container {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.management-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.management-header h2 {
  margin: 0;
  color: #333;
  font-size: 24px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.module-tabs {
  margin-bottom: 20px;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.module-tabs .el-button-group {
  display: flex;
  gap: 0;
}

.module-tabs .el-button {
  border-radius: 0;
  margin-right: 0;
}

.module-tabs .el-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.module-tabs .el-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.filter-section {
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 6px;
}

.search-form {
  margin: 0;
}

.search-form .el-form-item {
  margin-bottom: 0;
  margin-right: 12px;
}

.xinwenke-category-select {
  width: 160px;
}

.xinwenke-sort-select {
  width: 140px;
  margin-right: 8px;
}

.xinwenke-search-buttons .el-form-item__content {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.sort-and-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  flex-shrink: 0;
}

.batch-download-btn {
  position: relative;
  padding-right: 32px;
  min-width: 110px;
}

.batch-badge {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translate(50%, -50%);
}

/* 覆盖 ElBadge 内部圆角 */
:deep(.batch-badge .el-badge__content) {
  border-radius: 6px;
}


.file-list-section {
  margin-bottom: 20px;
}

/* 新文科模块特殊处理 */
.file-management-container .file-list-section .el-table {
  max-width: 100%;
  overflow-x: auto;
}

/* 确保新文科模块表格不会影响整体布局 */
.file-management-container .file-list-section .el-table {
  table-layout: auto;
  min-width: 0;
}

/* 新文科模块响应式处理 */
@media (min-width: 1200px) {
  .file-management-container .file-list-section .el-table {
    max-width: calc(100vw - 300px);
  }
}

@media (max-width: 1199px) {
  .file-management-container .file-list-section .el-table {
    max-width: calc(100vw - 250px);
  }
}

@media (max-width: 768px) {
  .file-management-container .file-list-section .el-table {
    max-width: 100%;
  }
}

.file-list-section {
  width: 100%;
  overflow: hidden;
}

.file-list-section .el-table {
  table-layout: fixed;
  width: 100%;
  margin: 0;
}

.file-list-section .el-table .el-table__body-wrapper {
  overflow-x: hidden;
}

.file-list-section .el-table .el-table__header-wrapper {
  overflow-x: hidden;
}

.file-list-section .el-table .el-table__body,
.file-list-section .el-table .el-table__header {
  width: 100% !important;
}

.file-list-section .el-table .el-table__body tr,
.file-list-section .el-table .el-table__header tr {
  width: 100%;
}

.file-list-section .el-table .el-table__body td,
.file-list-section .el-table .el-table__header th {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 8px 4px;
}

/* 确保表格列完全填充，避免多余空白 */
.file-list-section .el-table .el-table__body tr:last-child td:last-child,
.file-list-section .el-table .el-table__header tr th:last-child {
  border-right: none;
}

/* 确保状态列有足够的空间 */
.file-list-section .el-table .el-table__body td:nth-last-child(2),
.file-list-section .el-table .el-table__header th:nth-last-child(2) {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
}

/* 操作列特殊处理 - 消除右侧空白 */
.file-list-section .el-table .el-table__body td:last-child,
.file-list-section .el-table .el-table__header th:last-child {
  padding-right: 0;
}

.file-list-section .el-table th {
  text-align: center;
  background-color: #f5f7fa;
  font-weight: 600;
}

.file-list-section .el-table td {
  text-align: center;
  vertical-align: middle;
}

/* 确保表格完全撑满父容器，消除右侧空白 */
.file-list-section .el-table .el-table__body-wrapper,
.file-list-section .el-table .el-table__header-wrapper {
  width: 100%;
}

.file-list-section .el-table .el-table__body-wrapper::-webkit-scrollbar {
  display: none;
}

/* 表格边框处理，确保右侧无多余边框 */
.file-list-section .el-table {
  border-right: none;
}

.file-list-section .el-table .el-table__body tr td:last-child,
.file-list-section .el-table .el-table__header tr th:last-child {
  border-right: none;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.price {
  color: #f56c6c;
  font-weight: 600;
}

.no-cover {
  color: #999;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 40px;
  background: #f5f5f5;
  border-radius: 4px;
  border: 1px dashed #ddd;
}

.file-detail {
  padding: 10px 0;
}

.file-actions {
  text-align: center;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  flex-wrap: nowrap;
  white-space: nowrap;
  width: 100%;
  padding: 0 2px;
  box-sizing: border-box;
}

.action-btn {
  min-width: 48px;
  padding: 4px 6px;
  font-size: 11px;
  line-height: 1.2;
  margin: 0;
  flex-shrink: 0;
}

.action-btn + .action-btn {
  margin-left: 1px;
}

/* 文件预览对话框样式 */
.file-preview-dialog .el-dialog {
  margin: 0 auto !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.file-preview-dialog .el-dialog__body {
  flex: 1;
  padding: 0;
  overflow: hidden;
}

.file-preview-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.file-info-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafbfc;
  flex-shrink: 0;
}

.file-basic-info {
  flex: 1;
  margin-right: 20px;
}

.file-title-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.file-title {
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-meta-row {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.file-format,
.file-author,
.file-price,
.file-pages {
  color: #666;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.file-format {
  background: #f0f9ff;
  color: #1890ff;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.file-price {
  color: #f56c6c;
  font-weight: 600;
}

.file-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.file-actions .el-button {
  padding: 8px 16px;
  font-size: 14px;
}

.file-preview-area {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #f8f9fa;
}

.preview-images {
  height: 100%;
  background: #f8f9fa;
}

.preview-carousel {
  height: 100%;
}

.preview-image-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 16px;
  box-sizing: border-box;
  position: relative;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: optimize-contrast;
  -ms-interpolation-mode: nearest-neighbor;
  display: block;
  margin: 0 auto;
}

.preview-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

/* 确保图片清晰度 */
.preview-image-container {
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: optimize-contrast;
}

/* 确保图片容器有足够的空间 */
.preview-images .el-carousel__container {
  height: 100%;
}

.preview-images .el-carousel__item {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.online-preview {
  height: 100%;
  width: 100%;
  background: #fff;
}

.preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 6px;
}

.no-preview {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 40px;
}

.no-preview-content {
  text-align: center;
  color: #999;
  max-width: 300px;
}

.no-preview-content i {
  margin-bottom: 16px;
  display: block;
}

.no-preview-content p {
  margin: 12px 0;
  font-size: 14px;
  line-height: 1.5;
}

.no-preview-tip {
  font-size: 12px;
  color: #ccc;
  margin-top: 8px;
}

.preview-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
}

/* 上传文件对话框通用样式 */
.upload-dialog .el-dialog {
  margin: 0 auto !important;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 8vh;
  left: 50%;
  transform: translateX(-50%);
}

.upload-dialog .el-dialog__body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
  max-height: calc(85vh - 100px);
}

.upload-dialog .el-dialog__header {
  padding: 15px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.upload-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.upload-dialog .el-dialog__close {
  font-size: 18px;
  color: #999;
}

.upload-dialog .el-dialog__close:hover {
  color: #409eff;
}

/* 确保模态框固定在屏幕中央 */
.upload-dialog .el-dialog__wrapper {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
}

/* 编辑对话框样式 */
.edit-dialog .el-dialog {
  margin: 0 auto !important;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 1vh;
  left: 50%;
  transform: translateX(-50%);
}

.edit-dialog .el-dialog__body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
  max-height: calc(85vh - 100px);
}

.edit-dialog .el-dialog__header {
  padding: 15px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.edit-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.edit-dialog .el-dialog__close {
  font-size: 18px;
  color: #999;
}

.edit-dialog .el-dialog__close:hover {
  color: #409eff;
}

/* 确保编辑模态框固定在屏幕中央 */
.edit-dialog .el-dialog__wrapper {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
}

/* 批量下载对话框样式 */
.batch-download-dialog .el-dialog {
  margin: 0 auto !important;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 10vh;
  left: 50%;
  transform: translateX(-50%);
}

.batch-download-dialog .el-dialog__body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(70vh - 100px);
}

.batch-download-dialog .el-dialog__header {
  padding: 15px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.batch-download-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.batch-download-dialog .el-dialog__close {
  font-size: 18px;
  color: #999;
}

.batch-download-dialog .el-dialog__close:hover {
  color: #409eff;
}

/* 批量下载内容样式 */
.batch-download-content {
  max-height: 50vh;
  overflow-y: auto;
}

.selected-files-section,
.download-info-section,
.download-status-section {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
}

.selected-files-section h4,
.download-info-section h4,
.download-status-section h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.selected-files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-tag {
  margin: 2px;
  max-width: 200px;
}

/* 确保批量下载模态框固定在屏幕中央 */
.batch-download-dialog .el-dialog__wrapper {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .file-management-container {
    padding: 15px;
  }
  
  .management-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .search-form .el-form-item {
    margin-right: 10px;
    margin-bottom: 10px;
  }
  
  .filter-section {
    padding: 15px;
  }
  
  /* 新文科模块搜索按钮样式 */
  .xinwenke-search-buttons .el-form-item__content {
    display: flex;
    align-items: center;
  }
  
  .sort-and-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .sort-and-buttons .el-button {
    margin-left: 0;
  }
  
  /* 排序选择框样式 */
  .search-form .el-form-item .el-select {
    width: 150px;
  }
  
  .search-form .el-form-item .el-select .el-input__inner {
    font-size: 13px;
  }
  
  /* 上传文件对话框样式 */
  .upload-dialog .el-dialog {
    margin: 0 auto !important;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 8vh;
    left: 50%;
    transform: translateX(-50%);
  }
  
  .upload-dialog .el-dialog__body {
    padding: 0;
    flex: 1;
    overflow-y: auto;
    max-height: calc(85vh - 100px);
  }
  
  .upload-dialog .el-dialog__header {
    padding: 15px 20px 10px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
  }
  
  .upload-dialog .el-dialog__title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }
  
  .upload-dialog .el-dialog__close {
    font-size: 18px;
    color: #999;
  }
  
  .upload-dialog .el-dialog__close:hover {
    color: #409eff;
  }
  
  /* 确保模态框固定在屏幕中央 */
  .upload-dialog .el-dialog__wrapper {
    position: fixed !important;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2000;
  }
  
  /* 移动端操作按钮适配 */
  .action-buttons {
    flex-direction: column;
    gap: 3px;
  }
  
  .action-btn {
    width: 100%;
    min-width: auto;
    padding: 3px 6px;
    font-size: 11px;
  }
  
  .action-btn + .action-btn {
    margin-left: 0;
    margin-top: 1px;
  }
  
  /* 移动端预览对话框适配 */
  .file-preview-dialog .el-dialog {
    width: 95% !important;
    margin: 2.5vh auto !important;
    max-height: 95vh;
  }
  
  .file-info-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
  }
  
  .file-basic-info {
    margin-right: 0;
    width: 100%;
  }
  
  .file-title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .file-title {
    font-size: 15px;
    white-space: normal;
    line-height: 1.3;
  }
  
  .file-meta-row {
    gap: 12px;
    flex-wrap: wrap;
  }
  
  .file-format {
    padding: 1px 6px;
    font-size: 12px;
  }
  
  .file-actions {
    width: 100%;
    justify-content: flex-end;
    gap: 8px;
  }
  
  .file-actions .el-button {
    padding: 6px 12px;
    font-size: 13px;
  }

  
  .preview-image-container {
    padding: 12px;
  }
}

/* 片段管理对话框样式 */
.fragment-management-dialog .el-dialog {
  margin: 0 auto !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.fragment-management-dialog .el-dialog__body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(90vh - 100px);
}

.fragment-management-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.current-file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.current-file-info h4 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  font-weight: 600;
}

.file-type-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.file-type-badge.ppt {
  background: #409eff;
  color: #fff;
}

.file-type-badge.word {
  background: #67c23a;
  color: #fff;
}

.fragment-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e4e7ed;
}

.fragment-list-section {
  width: 100%;
}

.fragment-pagination {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.fragment-action-btn {
  margin: 0 3px;
  padding: 4px 8px;
  font-size: 12px;
}

/* 片段表单对话框样式 */
.fragment-form-dialog .el-dialog {
  margin: 0 auto !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 5vh;
  left: 50%;
  transform: translateX(-50%);
}

.fragment-form-dialog .el-dialog__body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
  max-height: calc(90vh - 100px);
}

.fragment-form-dialog .el-dialog__header {
  padding: 15px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.fragment-form-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.fragment-form-dialog .el-dialog__close {
  font-size: 18px;
  color: #999;
}

.fragment-form-dialog .el-dialog__close:hover {
  color: #409eff;
}

/* 确保片段管理模态框固定在屏幕中央 */
.fragment-management-dialog .el-dialog__wrapper,
.fragment-form-dialog .el-dialog__wrapper {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
}
</style>
